import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Header from '~/Components/Header/Header';
import {
  listarSessoesEmRevisao,
  listarMovimentosSessao,
  marcarProcessadoConciliacao,
  lancarConciliacao,
  descartarSessaoConciliacao,
} from '~/Services/Service';

import HeaderUpload from './components/HeaderUpload';
import ListaSessoes from './components/ListaSessoes';
import GridMovimentos from './components/GridMovimentos';
import DrawerContrapartida from './components/DrawerContrapartida';
import FooterLancamento from './components/FooterLancamento';
import ModalConfirmarLancamento from './components/ModalConfirmarLancamento';
import { extrairMensagemErro } from './helpers';

const ConciliacaoBancariaGeral = () => {
  const empreendimentosRedux = useSelector((state) => state.empreendimentos);
  const [sessoes, setSessoes] = useState([]);
  const [sessaoAtiva, setSessaoAtiva] = useState(null);
  const [movimentos, setMovimentos] = useState([]);
  const [loadingSessoes, setLoadingSessoes] = useState(false);
  const [loadingMovimentos, setLoadingMovimentos] = useState(false);
  const [movimentoEdicao, setMovimentoEdicao] = useState(null);
  const [confirmarLancamento, setConfirmarLancamento] = useState(false);
  const [lancando, setLancando] = useState(false);
  const [descartando, setDescartando] = useState(false);

  const empreendimentosMap = useMemo(() => {
    const lista =
      empreendimentosRedux && empreendimentosRedux.sort
        ? empreendimentosRedux.sort
        : [];
    const mapa = new Map();
    lista.forEach((item) => mapa.set(item.codigo, item.descricao));
    return mapa;
  }, [empreendimentosRedux]);

  const enriquecerSessao = useCallback(
    (s) =>
      s && empreendimentosMap.has(s.codigo_grupo_empresa)
        ? {
            ...s,
            apelido_ge: empreendimentosMap.get(s.codigo_grupo_empresa),
          }
        : s,
    [empreendimentosMap]
  );

  const recarregarSessoes = useCallback(async () => {
    setLoadingSessoes(true);
    try {
      const resposta = await listarSessoesEmRevisao();
      const lista = Array.isArray(resposta)
        ? resposta
        : resposta && resposta.sessoes
          ? resposta.sessoes
          : [];
      setSessoes(lista.map(enriquecerSessao));
    } catch (err) {
      const mensagem = extrairMensagemErro(
        err,
        'Falha ao listar sessões em revisão.'
      );
      toast.error(mensagem);
      setSessoes([]);
    } finally {
      setLoadingSessoes(false);
    }
  }, [enriquecerSessao]);

  useEffect(() => {
    recarregarSessoes();
  }, [recarregarSessoes]);

  // LICOES Ciclo 9 L3 — flag ativo previne race quando o operador troca de sessão rapidamente.
  useEffect(() => {
    if (!sessaoAtiva) {
      setMovimentos([]);
      return undefined;
    }
    let ativo = true;
    setLoadingMovimentos(true);
    listarMovimentosSessao(sessaoAtiva.codigo)
      .then((resposta) => {
        if (!ativo) return;
        const lista = Array.isArray(resposta)
          ? resposta
          : resposta && resposta.movimentos
            ? resposta.movimentos
            : [];
        setMovimentos(lista);
      })
      .catch((err) => {
        if (!ativo) return;
        const mensagem = extrairMensagemErro(
          err,
          'Falha ao carregar movimentos da sessão.'
        );
        toast.error(mensagem);
        setMovimentos([]);
      })
      .finally(() => {
        if (ativo) setLoadingMovimentos(false);
      });
    return () => {
      ativo = false;
    };
  }, [sessaoAtiva]);

  const handleImportado = async (resposta) => {
    await recarregarSessoes();
    if (resposta && resposta.codigo_sessao) {
      setSessaoAtiva({
        codigo: resposta.codigo_sessao,
        codigo_grupo_empresa: resposta.codigo_grupo_empresa,
        banco_origem: resposta.banco_origem,
        total_movimentos: resposta.total_movimentos,
        status: 'R',
      });
    }
  };

  const recarregarMovimentos = useCallback(async () => {
    if (!sessaoAtiva) return;
    setLoadingMovimentos(true);
    try {
      const resposta = await listarMovimentosSessao(sessaoAtiva.codigo);
      const lista = Array.isArray(resposta)
        ? resposta
        : resposta && resposta.movimentos
          ? resposta.movimentos
          : [];
      setMovimentos(lista);
    } catch (err) {
      const mensagem = extrairMensagemErro(
        err,
        'Falha ao recarregar movimentos.'
      );
      toast.error(mensagem);
    } finally {
      setLoadingMovimentos(false);
    }
  }, [sessaoAtiva]);

  const handleToggleLanca = async (movimento) => {
    if (!sessaoAtiva) return;
    const novoLanca = movimento.lanca === 'S' ? 'N' : 'S';
    try {
      await marcarProcessadoConciliacao(sessaoAtiva.codigo, {
        ordem: movimento.ordem,
        lanca: novoLanca,
      });
      setMovimentos((atual) =>
        atual.map((m) =>
          m.ordem === movimento.ordem ? { ...m, lanca: novoLanca } : m
        )
      );
    } catch (err) {
      const mensagem = extrairMensagemErro(
        err,
        'Falha ao alternar marcação de lançamento.'
      );
      toast.error(mensagem);
    }
  };

  const handleLancar = async () => {
    if (!sessaoAtiva) return;
    setLancando(true);
    try {
      const resposta = await lancarConciliacao(sessaoAtiva.codigo);
      const linhas =
        resposta && resposta.linhas_lancadas != null
          ? resposta.linhas_lancadas
          : 0;
      toast.success(
        `Lançamento concluído. ${linhas} movimento(s) registrados no razão.`
      );
      setConfirmarLancamento(false);
      setSessaoAtiva(null);
      setMovimentos([]);
      await recarregarSessoes();
    } catch (err) {
      const mensagem = extrairMensagemErro(
        err,
        'Falha no lançamento — nenhum movimento foi gravado.'
      );
      toast.error(mensagem);
    } finally {
      setLancando(false);
    }
  };

  const handleDescartar = async (sessao) => {
    const alvo = sessao || sessaoAtiva;
    if (!alvo) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Descartar a sessão ${alvo.codigo}? Os dados ficam preservados para auditoria, mas a sessão não poderá mais ser lançada.`
      )
    ) {
      return;
    }
    setDescartando(true);
    try {
      await descartarSessaoConciliacao(alvo.codigo);
      toast.success(`Sessão ${alvo.codigo} descartada.`);
      if (sessaoAtiva && sessaoAtiva.codigo === alvo.codigo) {
        setSessaoAtiva(null);
        setMovimentos([]);
      }
      await recarregarSessoes();
    } catch (err) {
      const mensagem = extrairMensagemErro(err, 'Falha ao descartar sessão.');
      toast.error(mensagem);
    } finally {
      setDescartando(false);
    }
  };

  const totalProntos = useMemo(
    () =>
      movimentos.filter(
        (m) => m.lanca === 'S' && m.descricao !== 'SALDO ATUAL'
      ).length,
    [movimentos]
  );

  const saldoFinalEstimado = useMemo(() => {
    if (!movimentos || movimentos.length === 0) return null;
    const ultimoComSaldo = [...movimentos]
      .reverse()
      .find((m) => m.saldo != null);
    return ultimoComSaldo ? Number(ultimoComSaldo.saldo) : null;
  }, [movimentos]);

  const renderConteudoSessao = () => {
    if (loadingMovimentos) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" role="status" />
          <div className="mt-2 text-muted">Carregando movimentos...</div>
        </div>
      );
    }
    return (
      <>
        <GridMovimentos
          movimentos={movimentos}
          onConfigurarLinha={setMovimentoEdicao}
          onToggleLanca={handleToggleLanca}
        />
        <FooterLancamento
          totalProntos={totalProntos}
          saldoFinalEstimado={saldoFinalEstimado}
          podeLancar={totalProntos > 0 && !lancando}
          podeDescartar={!lancando && !descartando}
          onLancar={() => setConfirmarLancamento(true)}
          onDescartar={() => handleDescartar(sessaoAtiva)}
          lancando={lancando}
        />
      </>
    );
  };

  return (
    <>
      <Header title="Conciliação Bancária - Geral" />
      <Container fluid className="mt-3 mb-4">
        {sessaoAtiva ? (
          <>
            <div className="d-flex align-items-center mb-3">
              <Button
                variant="link"
                className="pl-0"
                onClick={() => {
                  setSessaoAtiva(null);
                  setMovimentos([]);
                }}
                disabled={lancando}
              >
                <FaArrowLeft className="mr-1" /> Voltar à listagem
              </Button>
              <div className="ml-3 text-muted small">
                Sessão <strong>{sessaoAtiva.codigo}</strong>{' '}
                {sessaoAtiva.banco_origem
                  ? `• Banco ${sessaoAtiva.banco_origem}`
                  : ''}{' '}
                {sessaoAtiva.codigo_grupo_empresa
                  ? `• GE ${sessaoAtiva.codigo_grupo_empresa}`
                  : ''}
              </div>
            </div>
            {renderConteudoSessao()}
          </>
        ) : (
          <>
            <HeaderUpload
              onImportado={handleImportado}
              disabled={loadingSessoes}
            />
            {loadingSessoes ? (
              <div className="text-center py-3">
                <Spinner animation="border" role="status" size="sm" />
                <span className="ml-2 text-muted small">
                  Carregando sessões...
                </span>
              </div>
            ) : (
              <ListaSessoes
                sessoes={sessoes}
                onRetomar={(s) => setSessaoAtiva(s)}
                onDescartar={handleDescartar}
                loading={descartando}
              />
            )}
            <Alert variant="info" className="small mb-0">
              Selecione o empreendimento e o arquivo CNAB240 (Caixa, Itaú,
              Bradesco ou Santander) para iniciar uma conciliação. As sessões
              em andamento ficam preservadas — você pode fechar o navegador e
              retomar mais tarde.
            </Alert>
          </>
        )}
      </Container>

      <DrawerContrapartida
        show={!!movimentoEdicao}
        onHide={() => setMovimentoEdicao(null)}
        movimento={movimentoEdicao}
        codigoSessao={sessaoAtiva ? sessaoAtiva.codigo : null}
        codigoGrupoEmpresa={
          sessaoAtiva ? sessaoAtiva.codigo_grupo_empresa : null
        }
        onContrapartidaSalva={() => {
          setMovimentoEdicao(null);
          recarregarMovimentos();
        }}
      />

      <ModalConfirmarLancamento
        show={confirmarLancamento}
        onHide={() =>
          lancando ? null : setConfirmarLancamento(false)
        }
        onConfirmar={handleLancar}
        totalProntos={totalProntos}
        lancando={lancando}
      />
    </>
  );
};

export default ConciliacaoBancariaGeral;
